<template>
    <div class="users-container">
        <!-- Stats Cards -->
        <div class="stats-grid">
            <div class="stat-card">
                <div class="stat-icon" style="background: #e0e7ff; color: #4f46e5;">
                    <i class="fas fa-users"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ users.length }}</div>
                    <div class="stat-label">Total Utilisateurs</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #dcfce7; color: #16a34a;">
                    <i class="fas fa-user-check"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ activeUsersCount }}</div>
                    <div class="stat-label">Utilisateurs Actifs</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #fef9c3; color: #ca8a04;">
                    <i class="fas fa-user-times"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ inactiveUsersCount }}</div>
                    <div class="stat-label">Utilisateurs Inactifs</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #dbeafe; color: #2563eb;">
                    <i class="fas fa-layer-group"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ availableGroups.length }}</div>
                    <div class="stat-label">Groupes</div>
                </div>
            </div>
        </div>

        <!-- Filters and Actions Bar -->
        <div class="filters-bar">
            <div class="filters-left">
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Rechercher un utilisateur..."
                    >
                </div>
                <select class="filter-select" v-model="filterStatus">
                    <option value="">Tous les statuts</option>
                    <option value="1">Actifs</option>
                    <option value="0">Inactifs</option>
                </select>
                <select class="filter-select" v-model="filterGroup">
                    <option value="">Tous les groupes</option>
                    <option v-for="group in availableGroups" :key="group.id" :value="group.id">
                        {{ group.name }}
                    </option>
                </select>
            </div>
            <div class="filters-right">
                <button class="btn-create" @click="showCreateForm = true">
                    <i class="fas fa-plus"></i>
                    Créer un utilisateur
                </button>
            </div>
        </div>

        <!-- Table Card -->
        <div class="table-card">
            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Groupes</th>
                            <th>Fuseau horaire</th>
                            <th>Statut</th>
                            <th>Rôle</th>
                            <th class="actions-header">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="filteredUsers.length === 0">
                            <td colspan="7" class="empty-cell">
                                <div class="empty-state">
                                    <i class="fas fa-inbox"></i>
                                    <p>Aucun utilisateur trouvé</p>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="user in paginatedUsers" :key="user.id" class="table-row">
                            <td>
                                <div class="user-name">{{ user.name }}</div>
                            </td>
                            <td>{{ user.email }}</td>
                            <td>
                                <div v-if="user.groupsname && user.groupsname.length > 0" class="group-tags">
                                    <span
                                        v-for="(group, index) in user.groupsname"
                                        :key="index"
                                        class="group-tag"
                                        :class="{ 'no-group-tag': group === 'Sans groupe' }"
                                    >
                                        <i v-if="group === 'Sans groupe'" class="fas fa-inbox"></i>
                                        {{ group }}
                                    </span>
                                </div>
                                <span v-else class="no-group-badge">
                                    <i class="fas fa-inbox"></i>
                                    Aucun groupe
                                </span>
                            </td>
                            <td>
                                <span class="timezone">{{ formatTimezone(user.timezone) }}</span>
                            </td>
                            <td>
                                <span
                                    class="status-badge"
                                    :class="user.is_active ? 'active' : 'inactive'"
                                >
                                    {{ user.is_active ? 'Actif' : 'Inactif' }}
                                </span>
                            </td>
                            <td>
                                <span
                                    v-if="user.is_admin"
                                    class="admin-badge"
                                    title="Administrateur"
                                >
                                    <i class="fas fa-shield-alt"></i>
                                    Admin
                                </span>
                                <span v-else class="text-muted">Utilisateur</span>
                            </td>
                            <td class="actions-cell">
                                <div class="action-buttons">
                                    <button
                                        class="action-btn"
                                        @click="viewUser(user)"
                                        title="Voir"
                                    >
                                        <i class="fas fa-eye"></i>
                                    </button>
                                    <button
                                        class="action-btn"
                                        @click="editUser(user)"
                                        title="Modifier"
                                    >
                                        <i class="fas fa-pen"></i>
                                    </button>
                                    <button
                                        class="action-btn action-btn-danger"
                                        @click="confirmDelete(user)"
                                        title="Supprimer"
                                    >
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="pagination" v-if="filteredUsers.length > pageSize">
                <div class="pagination-info">
                    Affichage de {{ startIndex + 1 }} à {{ Math.min(endIndex, filteredUsers.length) }}
                    sur {{ filteredUsers.length }} utilisateurs
                </div>
                <div class="pagination-controls">
                    <button
                        class="pagination-btn"
                        :disabled="currentPage === 1"
                        @click="currentPage--"
                    >
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="pagination-pages">
                        Page {{ currentPage }} / {{ totalPages }}
                    </span>
                    <button
                        class="pagination-btn"
                        :disabled="currentPage === totalPages"
                        @click="currentPage++"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Formulaire de création/édition -->
        <UsersForm
            v-if="showCreateForm || showEditForm"
            :user="currentUser"
            :groups="availableGroups"
            :mode="showEditForm ? 'edit' : 'create'"
            @close="closeForm"
            @saved="onUserSaved"
        />

        <!-- Modal de visualisation -->
        <div
            v-if="showViewModal"
            class="modal-overlay"
            @click="showViewModal = false"
        >
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title">
                        <i class="fas fa-user"></i>
                        Détails de l'utilisateur
                    </h3>
                    <button class="modal-close" @click="showViewModal = false">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body" v-if="currentUser">
                    <div class="user-details">
                        <div class="detail-item">
                            <span class="detail-label">Nom</span>
                            <span class="detail-value">{{ currentUser.name }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Email</span>
                            <span class="detail-value">{{ currentUser.email }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Groupes</span>
                            <span class="detail-value">
                                <div v-if="currentUser.groupsname && currentUser.groupsname.length > 0" class="group-tags">
                                    <span
                                        v-for="(group, index) in currentUser.groupsname"
                                        :key="index"
                                        class="group-tag"
                                        :class="{ 'no-group-tag': group === 'Sans groupe' }"
                                    >
                                        <i v-if="group === 'Sans groupe'" class="fas fa-inbox"></i>
                                        {{ group }}
                                    </span>
                                </div>
                                <span v-else class="no-group-badge">
                                    <i class="fas fa-inbox"></i>
                                    Aucun groupe
                                </span>
                            </span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Fuseau horaire</span>
                            <span class="detail-value">{{ formatTimezone(currentUser.timezone) }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Statut</span>
                            <span class="detail-value">
                                <span
                                    class="status-badge"
                                    :class="currentUser.is_active ? 'status-active' : 'status-inactive'"
                                >
                                    {{ currentUser.is_active ? 'Actif' : 'Inactif' }}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal de confirmation de suppression -->
        <div
            v-if="showDeleteModal"
            class="modal-overlay"
            @click="showDeleteModal = false"
        >
            <div class="modal-content modal-sm" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title text-danger">
                        <i class="fas fa-exclamation-triangle"></i>
                        Confirmer la suppression
                    </h3>
                    <button class="modal-close" @click="showDeleteModal = false">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body" v-if="currentUser">
                    <p>Êtes-vous sûr de vouloir supprimer cet utilisateur ?</p>
                    <div class="alert alert-warning">
                        <strong>{{ currentUser.name }}</strong><br>
                        <small>{{ currentUser.email }}</small>
                    </div>
                    <p class="text-muted mb-0">Cette action est irréversible.</p>
                </div>
                <div class="modal-footer">
                    <button class="btn-secondary" @click="showDeleteModal = false">
                        Annuler
                    </button>
                    <button class="btn-danger" @click="deleteUser">
                        <i class="fas fa-trash"></i>
                        Supprimer
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { useToast } from 'vue-toastification';
import fetch from '@/utils/fetch';
import UsersForm from './UsersForm.vue';

export default {
    name: 'UsersTable',
    components: {
        UsersForm
    },
    data() {
        return {
            users: [],
            availableGroups: [],
            searchQuery: '',
            filterStatus: '',
            filterGroup: '',
            currentPage: 1,
            pageSize: 30,
            currentUser: null,
            showCreateForm: false,
            showEditForm: false,
            showViewModal: false,
            showDeleteModal: false
        };
    },
    computed: {
        filteredUsers() {
            let users = [...this.users];

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                users = users.filter(user =>
                    (user.name || '').toLowerCase().includes(query) ||
                    (user.email || '').toLowerCase().includes(query)
                );
            }

            if (this.filterStatus !== '') {
                users = users.filter(user => user.is_active == this.filterStatus);
            }

            if (this.filterGroup !== '') {
                users = users.filter(user => {
                    if (!user.groups || user.groups.length === 0) return false;
                    return user.groups.includes(parseInt(this.filterGroup));
                });
            }

            return users;
        },
        totalPages() {
            return Math.ceil(this.filteredUsers.length / this.pageSize);
        },
        startIndex() {
            return (this.currentPage - 1) * this.pageSize;
        },
        endIndex() {
            return this.startIndex + this.pageSize;
        },
        paginatedUsers() {
            return this.filteredUsers.slice(this.startIndex, this.endIndex);
        },
        activeUsersCount() {
            return this.users.filter(u => u.is_active).length;
        },
        inactiveUsersCount() {
            return this.users.filter(u => !u.is_active).length;
        }
    },
    mounted() {
        this.loadData();
    },
    methods: {
        ...mapActions({
            deleteUserAction: 'users/delete/destroy'
        }),

        async loadData() {
            try {
                // Charger les utilisateurs
                const usersResponse = await fetch({ method: 'GET', url: 'users' });
                this.users = usersResponse.data.data || usersResponse.data || [];

                // Charger les groupes directement depuis l'API device-groups (comme DevicesTable)
                const groupsResponse = await fetch({ method: 'GET', url: 'device-groups' });
                const groupsData = groupsResponse.data.data || groupsResponse.data || [];

                // S'assurer que c'est un tableau valide
                this.availableGroups = Array.isArray(groupsData) ? groupsData : [];

                console.log('Utilisateurs chargés:', this.users.length);
                console.log('Groupes disponibles:', this.availableGroups.length, this.availableGroups);
            } catch (error) {
                console.error('Erreur lors du chargement:', error);
                const toast = useToast();
                toast.error('Erreur lors du chargement des données');
            }
        },

        resetFilters() {
            this.searchQuery = '';
            this.filterStatus = '';
            this.filterGroup = '';
            this.currentPage = 1;
        },

        viewUser(user) {
            this.currentUser = user;
            this.showViewModal = true;
        },

        editUser(user) {
            this.currentUser = { ...user };
            this.showEditForm = true;
        },

        confirmDelete(user) {
            this.currentUser = user;
            this.showDeleteModal = true;
        },

        async deleteUser() {
            const toast = useToast();
            try {
                await this.deleteUserAction(this.currentUser.id);
                toast.success('Utilisateur supprimé avec succès');
                this.showDeleteModal = false;
                this.currentUser = null;
                await this.loadData();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                toast.error('Erreur lors de la suppression');
            }
        },

        closeForm() {
            this.showCreateForm = false;
            this.showEditForm = false;
            this.currentUser = null;
        },

        async onUserSaved() {
            this.closeForm();
            await this.loadData();
            // Pas de toast ici, car UsersForm en affiche déjà un
        },

        formatTimezone(timezone) {
            if (timezone === null || timezone === undefined) return 'GMT±00:00';
            const offset = parseFloat(timezone);
            const sign = offset >= 0 ? '+' : '';
            return `GMT${sign}${offset}:00`;
        }
    },
    watch: {
        searchQuery() { this.currentPage = 1; },
        filterStatus() { this.currentPage = 1; },
        filterGroup() { this.currentPage = 1; }
    }
};
</script>

<style scoped>
.users-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
}

.stat-card {
    background: white;
    border-radius: 0.75rem;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
}

.stat-content {
    flex: 1;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    line-height: 1;
    margin-bottom: 0.25rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
}

/* Filters Bar */
.filters-bar {
    background: white;
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
}

.search-box {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    min-width: 250px;
}

.search-box i {
    color: #9ca3af;
    margin-right: 0.5rem;
}

.search-box input {
    border: none;
    background: transparent;
    outline: none;
    flex: 1;
    font-size: 0.875rem;
    color: #111827;
}

.search-box input::placeholder {
    color: #9ca3af;
}

.filter-select {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 0.875rem;
    background: white;
    color: #374151;
    cursor: pointer;
    outline: none;
}

.filter-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn-create {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #4f46e5;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-create:hover {
    background: #4338ca;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

/* Table Card */
.table-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.table-wrapper {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.data-table th {
    padding: 0.75rem 1.5rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
}

.data-table th.actions-header {
    text-align: right;
}

.data-table td {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
}

.data-table tbody tr:hover {
    background: #f9fafb;
}

.data-table tbody tr:last-child td {
    border-bottom: none;
}

.user-name {
    font-weight: 600;
    color: #111827;
}

.group-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}

.group-tag {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    background: #e0e7ff;
    color: #4f46e5;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
}

.no-group-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #d1d5db;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
}

.no-group-tag {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%) !important;
    color: #92400e !important;
    border: 1px dashed #f59e0b !important;
}

.timezone {
    color: #6b7280;
    font-size: 0.875rem;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
}

.status-badge.active {
    background: #dcfce7;
    color: #16a34a;
}

.status-badge.inactive {
    background: #fee2e2;
    color: #dc2626;
}

.admin-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.text-muted {
    color: #9ca3af;
    font-size: 0.875rem;
}

.actions-cell {
    text-align: right;
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
}

.action-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.action-btn:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #111827;
}

.action-btn-danger:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #dc2626;
}

.empty-cell {
    padding: 0 !important;
}

.empty-state {
    padding: 4rem 2rem;
    text-align: center;
    color: #9ca3af;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state p {
    font-size: 0.875rem;
    margin: 0;
}

/* Pagination */
.pagination {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafafa;
}

.pagination-info {
    font-size: 0.875rem;
    color: #6b7280;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.pagination-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
}

.pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.pagination-pages {
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
    padding: 0 0.75rem;
}

/* Modal */
.modal-overlay {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    backdrop-filter: blur(2px);
}

.modal-content {
    background: white;
    border-radius: 1rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-content.modal-sm {
    max-width: 400px;
}

.modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.modal-close {
    width: 36px;
    height: 36px;
    border: none;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-close:hover {
    background: #e5e7eb;
    color: #111827;
}

.modal-body {
    padding: 1.5rem;
}

.modal-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

/* User Details */
.user-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.detail-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
}

.detail-value {
    font-size: 0.875rem;
    color: #111827;
}

.alert {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border-left: 4px solid;
}

.alert-warning {
    background: #fef3c7;
    border-color: #f59e0b;
}

/* Buttons */
.btn-secondary {
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
}

.btn-danger {
    background: #dc2626;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-danger:hover {
    background: #b91c1c;
}

/* Responsive */
@media (max-width: 1024px) {
    .filters-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .filters-left {
        flex-direction: column;
        align-items: stretch;
    }

    .search-box {
        min-width: 100%;
    }
}

@media (max-width: 768px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .data-table th,
    .data-table td {
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
    }

    .action-buttons {
        flex-direction: column;
        gap: 0.25rem;
    }

    .pagination {
        flex-direction: column;
        gap: 1rem;
    }
}

@media (max-width: 640px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .filters-bar {
        padding: 1rem;
    }
}
</style>
